<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ChevronDown, Building2, BookOpen } from 'lucide-vue-next';
import { schoolsData, getXinfaBySchoolId, type Xinfa } from '../data/schools';

// 胶水集成：使用 shadcn-vue 组件构建选择器 UI
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * 门派心法选择器组件
 * @description 支持二级联动的门派/心法选择器，使用 Popover 优化交互
 */

interface Props {
  modelValue: {
    schoolId: string;
    xinfaId: string;
  };
}

interface Emits {
  (e: 'update:modelValue', value: { schoolId: string; xinfaId: string }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isOpen = ref(false);
const selectedSchool = ref(props.modelValue.schoolId);
const selectedXinfa = ref(props.modelValue.xinfaId);

const currentXinfaList = computed<Xinfa[]>(() => {
  return getXinfaBySchoolId(selectedSchool.value);
});

const displayText = computed(() => {
  if (!selectedSchool.value) return '请选择门派和心法';
  const school = schoolsData.find(s => s.id === selectedSchool.value);
  const xinfa = currentXinfaList.value.find(x => x.id === selectedXinfa.value);
  if (school && xinfa) {
    return `${school.name} / ${xinfa.name}`;
  }
  return '请选择门派和心法';
});

const selectedSchoolName = computed(() => {
  const school = schoolsData.find(s => s.id === selectedSchool.value);
  return school?.name || '';
});

const selectedXinfaName = computed(() => {
  const xinfa = currentXinfaList.value.find(x => x.id === selectedXinfa.value);
  return xinfa?.name || '';
});

watch(() => props.modelValue, (newVal) => {
  selectedSchool.value = newVal.schoolId;
  selectedXinfa.value = newVal.xinfaId;
}, { deep: true });

const selectSchool = (schoolId: string) => {
  selectedSchool.value = schoolId;
  selectedXinfa.value = '';
};

const selectXinfa = (xinfaId: string) => {
  selectedXinfa.value = xinfaId;
  emitUpdate();
  isOpen.value = false;
};

const emitUpdate = () => {
  emit('update:modelValue', {
    schoolId: selectedSchool.value,
    xinfaId: selectedXinfa.value
  });
};
</script>

<template>
  <div class="space-y-4">
    <!-- 已选展示 -->
    <div v-if="selectedSchool && selectedXinfa" class="flex items-center gap-2 animate-fade-in">
      <Badge variant="secondary" class="gap-1">
        <Building2 class="w-3 h-3" />
        {{ selectedSchoolName }}
      </Badge>
      <span class="text-muted-foreground">/</span>
      <Badge variant="outline" class="gap-1 border-primary/30 text-primary">
        <BookOpen class="w-3 h-3" />
        {{ selectedXinfaName }}
      </Badge>
    </div>

    <!-- Popover 选择器 -->
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          role="combobox"
          :aria-expanded="isOpen"
          class="w-full justify-between h-12 px-4 border-border/60 hover:border-primary/50 hover:bg-secondary/50 transition-all duration-200"
        >
          <span :class="selectedSchool ? 'text-foreground' : 'text-muted-foreground'">
            {{ displayText }}
          </span>
          <ChevronDown class="w-4 h-4 opacity-50 transition-transform duration-200" :class="isOpen && 'rotate-180'" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent class="w-[360px] p-0" align="start">
        <div class="flex max-h-[360px]">
          <!-- 门派列表 -->
          <div class="w-1/2 border-r border-border/50">
            <div class="px-3 py-2 text-xs font-semibold text-muted-foreground bg-secondary/30 border-b border-border/50">
              选择门派
            </div>
            <div class="overflow-y-auto max-h-[300px]">
              <button
                v-for="school in schoolsData"
                :key="school.id"
                class="w-full px-3 py-2.5 text-left text-sm transition-colors duration-150 flex items-center gap-2"
                :class="selectedSchool === school.id 
                  ? 'bg-primary/10 text-primary font-medium' 
                  : 'hover:bg-secondary/50 text-foreground'"
                @click="selectSchool(school.id)"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="selectedSchool === school.id ? 'bg-primary' : 'bg-muted-foreground/30'"></span>
                {{ school.name }}
              </button>
            </div>
          </div>
          
          <!-- 心法列表 -->
          <div class="w-1/2">
            <div class="px-3 py-2 text-xs font-semibold text-muted-foreground bg-secondary/30 border-b border-border/50">
              选择心法
            </div>
            <div class="overflow-y-auto max-h-[300px] p-1">
              <div v-if="!selectedSchool" class="p-4 text-center text-sm text-muted-foreground">
                请先选择门派
              </div>
              <template v-else>
                <button
                  v-for="xinfa in currentXinfaList"
                  :key="xinfa.id"
                  class="w-full px-3 py-2.5 text-left text-sm rounded-md transition-all duration-150 flex items-center gap-2"
                  :class="selectedXinfa === xinfa.id 
                    ? 'bg-primary text-primary-foreground font-medium shadow-sm' 
                    : 'hover:bg-secondary text-foreground'"
                  @click="selectXinfa(xinfa.id)"
                >
                  <span v-if="selectedXinfa === xinfa.id" class="w-1.5 h-1.5 rounded-full bg-primary-foreground"></span>
                  <span v-else class="w-1.5 h-1.5 rounded-full bg-muted-foreground/30"></span>
                  {{ xinfa.name }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    <!-- 提示 -->
    <p class="text-xs text-muted-foreground flex items-center gap-1.5">
      <span class="w-1 h-1 rounded-full bg-primary/60"></span>
      请先选择门派，再选择对应的心法
    </p>
  </div>
</template>
